import { captureException } from '@sentry/nextjs';
import { DEFAULT_BANK_ACCOUNT_DESCRIPTION, DEFAULT_BANK_ACCOUNT_TITLE } from 'constants/user';
import {
  PaymentFulfillmentChannelsEnum,
  useUpdateUserDefaultCoachPaymentFulfillmentChannelMutation,
  useUpdateUserDidSkipStripeSetupMutation,
} from 'types/generated/client';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import ArrowRight from 'svg/ArrowRight';
import Cash from 'svg/Cash';
import Chart from 'svg/Chart';
import CheckBadge from 'svg/CheckBadge';
import CurrencyIcon from 'svg/CurrencyIcon';
import BankAccountPrompt from 'screens/CoachDetails/BankAccountPrompt';
import Button, { ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import ModalStripeOnboarding from 'components/modals/ModalStripeOnboarding';

const features = [
  {
    icon: <CheckBadge className="h-5 w-5" />,
    text: 'Fast, secure payouts via Stripe — funds land in your account automatically.',
  },
  {
    icon: <Cash className="h-5 w-5" />,
    text: 'Bookable profile: players can pay on the spot; no chasing invoices.',
  },
  {
    icon: <CurrencyIcon className="h-5 w-5" viewBox="0 0 32 32" />,
    text: 'Tips & extras: enable instant gratuities and add-ons at checkout.',
  },
  {
    icon: <Chart className="h-5 w-5" viewBox="0 0 16 16" />,
    text: 'One-tap income reports for easy tax time and growth tracking.',
  },
];

interface PaymentStepProps {
  onSkip: () => void;
}

const PaymentStep = ({ onSkip }: PaymentStepProps) => {
  const viewer = useViewer();
  const bankAccountModal = useModal();
  const payoutModal = useModal();

  const [updateUserDefaultCoachPaymentFulfillmentChannel] =
    useUpdateUserDefaultCoachPaymentFulfillmentChannelMutation();

  const [updateUserDidSkipStripeSetup, { loading: isSkipping }] =
    useUpdateUserDidSkipStripeSetupMutation();

  const handleClosePayoutModal = async (didAddBankInformation: boolean) => {
    payoutModal.closeModal();
    if (viewer.userId) {
      if (didAddBankInformation) {
        try {
          await updateUserDefaultCoachPaymentFulfillmentChannel({
            variables: {
              id: viewer.userId,
              defaultCoachPaymentFulfillmentChannel: PaymentFulfillmentChannelsEnum.OnPlatform,
            },
          });
        } catch (err) {
          captureException(err);
        }
      }
    }
  };

  const handleSkip = async () => {
    if (!viewer.userId) return;

    try {
      await updateUserDidSkipStripeSetup({
        variables: {
          id: viewer.userId,
          didSkipStripeSetup: true,
        },
      });
      onSkip();
    } catch (err) {
      captureException(err);
    }
  };

  return (
    <>
      <div>
        <div className="mt-[4.2rem] flex flex-row gap-16 max-md:mt-[3.75rem] max-md:flex-col max-md:gap-6">
          <h3 className="typography-product-heading-compact-desktop  shrink-0 text-color-text-lightmode-invert">
            Connect your bank account
          </h3>

          <div className="max-w-[40rem]">
            <p className="typography-product-element-label  text-color-text-darkmode-secondary">
              To keep Bounce safe and professional, we verify every coach up-front. Linking your
              bank blocks fraud, protects both coach and player, and shows athletes you're serious
              about your business.
            </p>
            <ul className="mt-6 flex flex-col gap-6 max-md:mt-4 max-md:gap-ds-md">
              {features.map((feature, idx) => (
                <li
                  key={idx}
                  className="max-md:typography-product-caption flex  gap-3 text-color-text-darkmode-placeholder"
                >
                  <span className="mt-0.5">{feature.icon}</span>
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-12 flex items-center justify-center gap-4 border-t border-color-border-card-darkmode pt-8 max-md:mt-ds-xl max-md:flex-col max-md:pt-ds-xl">
          <Button
            sizeDesktop="lg"
            sizeMobile="md"
            variant="brand-secondary"
            onClick={handleSkip}
            disabled={isSkipping}
            size="lg"
            className="w-fit"
          >
            I don’t want to collect payments
          </Button>
          <Button
            sizeDesktop="lg"
            sizeMobile="md"
            variant="brand"
            onClick={() => bankAccountModal.openModal()}
            size="lg"
            className="w-fit"
            disabled={isSkipping}
          >
            Connect your bank account
          </Button>
        </div>
        <div className="typography-product-caption max-sm:typography-product-chips-filters mt-ds-xl text-center text-color-text-darkmode-tertiary">
          Don't worry — you can edit your information anytime by clicking on your avatar and
          selecting 'My Listing' from the dropdown menu.
        </div>
      </div>

      <Modal isOpen={bankAccountModal.isOpen} handleClose={bankAccountModal.closeModal}>
        <BankAccountPrompt
          onConnectBankAccount={payoutModal.openModal}
          heading={DEFAULT_BANK_ACCOUNT_TITLE}
          description={DEFAULT_BANK_ACCOUNT_DESCRIPTION}
          showButton
        />
      </Modal>
      <ModalStripeOnboarding isOpen={payoutModal.isOpen} handleClose={handleClosePayoutModal} />
    </>
  );
};

export default PaymentStep;
